import { Link } from 'react-router-dom';
import { 
  QrCode, 
  Sparkles, 
  Download, 
  Share2, 
  Palette, 
  Shield,
  Zap,
  CheckCircle,
  ArrowRight,
  Star
} from 'lucide-react';
import Button from '@/components/common/Button';

/**
 * Home page component - Landing page for QR-it
 */
function HomePage() {
  const features = [
    {
      icon: Sparkles,
      title: 'Custom Designs',
      description: 'Choose from multiple styles, colors, and shapes to make your QR codes unique.',
    },
    {
      icon: Shield,
      title: 'Reliable Scanning',
      description: 'All designs are tested for scannability. Your QR codes will work every time.',
    },
    {
      icon: Download,
      title: 'Multiple Formats',
      description: 'Export your QR codes as high-quality PNG or SVG files for any use case.',
    },
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Generate QR codes instantly with real-time preview as you customize.',
    },
    {
      icon: Palette,
      title: 'Brand Integration',
      description: 'Add your logo and match your brand colors for professional results.',
    },
    {
      icon: Share2,
      title: 'Easy Sharing',
      description: 'Share your designs with a link or save them for later editing.',
    },
  ];

  const useCases = [
    { label: 'Website Links', icon: '🌐' },
    { label: 'Social Media', icon: '📱' },
    { label: 'Business Cards', icon: '💼' },
    { label: 'Payment Links', icon: '💳' },
    { label: 'WiFi Networks', icon: '📶' },
    { label: 'Event Tickets', icon: '🎟️' },
    { label: 'Restaurant Menus', icon: '🍽️' },
    { label: 'Contact Cards', icon: '👤' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-primary-50">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full opacity-20 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-200 rounded-full opacity-20 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100 mb-8">
              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
              <span className="text-sm font-medium text-gray-600">
                Free • No signup required
              </span>
            </div>

            {/* Main heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Create Beautiful
              <span className="text-gradient"> QR Codes </span>
              in Seconds
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto">
              Generate custom-designed QR codes for any URL or text. 
              Add your logo, choose colors, and download in high quality. 
              Free forever.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/generator">
                <Button size="xl" icon={Sparkles}>
                  Create QR Code
                  <ArrowRight className="w-5 h-5 ml-1" />
                </Button>
              </Link>
              <Link to="/templates">
                <Button variant="outline" size="xl">
                  Browse Templates
                </Button>
              </Link>
            </div>

            {/* Trust indicators */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                100% Free
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                No Watermarks
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                Unlimited Downloads
              </div>
            </div>
          </div>

          {/* Preview Image */}
          <div className="mt-16 max-w-4xl mx-auto">
            <div className="relative bg-white rounded-2xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
              <div className="bg-gray-100 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
              </div>
              <div className="p-8 flex items-center justify-center gap-8 flex-wrap">
                {/* Sample QR codes */}
                {[
                  { bg: '#000000', dot: 'rounded' },
                  { bg: '#0ea5e9', dot: 'dots' },
                  { bg: '#8b5cf6', dot: 'classy' },
                  { bg: '#ec4899', dot: 'extra-rounded' },
                ].map((style, i) => (
                  <div 
                    key={i}
                    className="w-24 h-24 rounded-xl shadow-lg flex items-center justify-center"
                    style={{ backgroundColor: style.bg }}
                  >
                    <QrCode className="w-16 h-16 text-white" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Everything you need to create perfect QR codes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Powerful features wrapped in a simple interface. No design skills required.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-gray-50 hover:bg-white hover:shadow-xl 
                           transition-all duration-300 border border-transparent hover:border-gray-100"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 
                                flex items-center justify-center mb-4 shadow-lg shadow-primary-500/25">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              QR codes for every use case
            </h2>
            <p className="text-lg text-gray-600">
              From personal to professional, we have got you covered
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {useCases.map((useCase, index) => (
              <div
                key={index}
                className="flex items-center gap-3 px-6 py-4 bg-white rounded-xl 
                           shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
              >
                <span className="text-2xl">{useCase.icon}</span>
                <span className="font-medium text-gray-700">{useCase.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to create your QR code?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            It takes less than a minute. No signup, no credit card, no hassle.
          </p>
          <Link to="/generator">
            <Button 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-50"
            >
              <QrCode className="w-5 h-5" />
              Start Creating Now
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
